import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppDayFold - folding and unfolding a DAY in the contact page's conversation, made to feel right (owner, 2026-09-28:
// "Collapsing and expanding days in the chat interface is not working well, it doesn't feel right").
//
// ⭐ WHAT WAS MEASURED IN REAL CHROME ON THE OWNER'S OWN PAGE (1280 and 390, published 1.1.1.x), and what this fixes:
//   1. THE CLICKED DAY LABEL JUMPED AWAY. `.chat` is `column-reverse` (it opens at the newest message), so the
//      conversation is anchored at its BOTTOM: folding a day moved its label DOWN by the day's height (+236 px,
//      +622 px - out of view, so the second click missed), unfolding moved it UP (-109 px) and the day spread upward
//      out of sight. And not even consistently: at 390 Chrome's own scroll anchoring sometimes held it (0 px) and
//      then threw it 293 px on the way back. ⭐ FIX: the label's on-screen top is noted BEFORE the change (the
//      click on the label, or Space on the focused checkbox - `keydown`, because a checkbox is toggled before its
//      own `click` is dispatched) and the conversation is scrolled after it (`Settle`, again one frame later for
//      any anchoring the browser applies) so the label stays under the pointer. Measured against the DAY's top,
//      not the label's: the label is sticky, and a sticky label reports where it is pinned, not where it lives.
//   2. A LINK INTO A FOLDED DAY (a task's "↗", a fact's anchor, `#c/<page>/m-…`) SHOWED ITS MESSAGES WITH THE ARROW
//      STILL SAYING "FOLDED" - the page opens it with `:has(.b:target)` but its checkbox stayed unticked, so the
//      first click on the label did nothing visible. ⭐ FIX: the target's day is ticked on load and on every
//      `hashchange` (`OpenTarget`).
//   3. EVERY REBUILD / REPAINT FOLDED THE DAYS BACK. The frame is rebuilt from the page each time the contact is
//      drawn, so a day the owner opened closed again. ⭐ FIX: the owner's own choices are remembered for the VISIT
//      per page (`Memory`, day id -> open) and applied when the page loads - only to days the page still has.
// ⚠ The page stays SCRIPT-FREE (a static file in the owner's Dropbox, opened on its own too): this attaches from
// OUTSIDE the `blob:` frame, like `AppReactions` and `AppTaskFrame`. Bubble ids and anchors are untouched.
// ⛔ NOTHING HERE SENDS OR WRITES ANYTHING - it only scrolls and ticks checkboxes in the frame.
// ⚠ SHAPED BY INTERFACES, so the suite drives it with a small fake document.

export interface IDfRect {
    top: number;
}

export interface IDfNode {
    id: string;
    checked: boolean;
    scrollTop: number;
    className: string;
    closest(Selector: string): IDfNode | null;
    querySelector(Selector: string): IDfNode | null;
    getBoundingClientRect(): IDfRect;
}

export interface IDfDoc {
    querySelector(Selector: string): IDfNode | null;
    querySelectorAll(Selector: string): ArrayLike<IDfNode>;
    getElementById(Id: string): IDfNode | null;
    addEventListener(Type: string, Listener: (Ev: Event) => void, Capture: boolean): void;
}

export interface IDfWin {
    location: { hash: string };
    addEventListener(Type: string, Listener: () => void): void;
    requestAnimationFrame(Callback: () => void): number;
}

export interface IDfFrame {
    contentDocument: IDfDoc | null;
    contentWindow: IDfWin | null;
    addEventListener(Type: string, Listener: () => void): void;
}

/** The label the owner is about to toggle, and where it was on screen. */
export class AppDayPending {
    public Box: IDfNode;
    public Day: IDfNode;
    public Top: number;

    public constructor(Box: IDfNode, Day: IDfNode, Top: number) {
        this.Box = Box;
        this.Day = Day;
        this.Top = Top;
    }
}

export class AppDayFold {
    /** page -> day checkbox id -> open, for this visit. */
    public static Memory: Record<string, Record<string, boolean>> = {};
    /** A day's checkbox id, as `DossierHtml.DayGroup` and `AppChatAppend.DayOf` write it. */
    public static readonly IdPattern: RegExp = new RegExp("^d-(wac-)?[0-9]{8}$");

    private Page: string;
    private Frame: IDfFrame | null = null;
    private Doc: IDfDoc | null = null;
    private Win: IDfWin | null = null;
    private Pending: AppDayPending | null = null;

    public constructor(Page: string) {
        this.Page = nz(Page);
    }

    public Attach = (Frame: IDfFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.Attach");
        }
    };

    /** The page is in: the owner's earlier choices back, the linked message's day ticked, the listeners on - once. */
    public OnLoad = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Frame === null || this.Frame.contentDocument === null || this.Doc !== null) { return; }
            this.Doc = this.Frame.contentDocument as IDfDoc;
            this.Win = this.Frame.contentWindow;
            this.Restore();
            this.OpenTarget();
            this.Doc.addEventListener("click", this.OnClick, true);
            this.Doc.addEventListener("keydown", this.OnKey, true);
            this.Doc.addEventListener("change", this.OnChange, false);
            if (this.Win !== null) { (this.Win as IDfWin).addEventListener("hashchange", this.OpenTarget); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.OnLoad");
        }
    };

    /** Days the owner opened or folded earlier this visit, as they left them. ⚠ Only ids the page still has. */
    private Restore = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Mine: Record<string, boolean> | undefined = AppDayFold.Memory[this.Page];
            if (Mine === undefined || this.Doc === null) { return; }
            const Boxes: ArrayLike<IDfNode> = (this.Doc as IDfDoc).querySelectorAll(".daybox");
            for (let At: number = 0; At < Boxes.length; At = At + 1) {
                const Id: string = nz(Boxes[At].id);
                if (Object.prototype.hasOwnProperty.call(Mine, Id) === true) { Boxes[At].checked = Mine[Id] === true; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.Restore");
        }
    };

    /** The message the address points at: its day is ticked, so the arrow and the next click agree with what shows. */
    public OpenTarget = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null || this.Win === null) { return; }
            const Hash: string = nz((this.Win as IDfWin).location.hash);
            if (Hash.length < 2) { return; }
            const Target: IDfNode | null = (this.Doc as IDfDoc).getElementById(AppDayFold.IdOf(Hash));
            if (Target === null) { return; }
            const Day: IDfNode | null = Target.closest(".day");
            if (Day === null) { return; }
            const Box: IDfNode | null = (Day as IDfNode).querySelector(".daybox");
            if (Box !== null) { (Box as IDfNode).checked = true; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.OpenTarget");
        }
    };

    /** A click on a day's pill: note where its label is BEFORE the checkbox changes. */
    public OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Node: IDfNode | null = AppDayFold.NodeOf(Ev);
            if (Node === null) { return; }
            const Head: IDfNode | null = Node.closest(".dayhead");
            if (Head === null) { return; }
            this.Note(Head as IDfNode);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.OnClick");
        }
    };

    /** Space on the focused checkbox (the keyboard's way to fold): noted before the toggle, as a click is. */
    public OnKey = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Key: string = nz((Ev as KeyboardEvent).key);
            if (Key !== " " && Key !== "Spacebar") { return; }
            const Node: IDfNode | null = AppDayFold.NodeOf(Ev);
            if (Node === null || nz(Node.className).indexOf("daybox") < 0) { return; }
            const Day: IDfNode | null = Node.closest(".day");
            if (Day === null) { return; }
            const Head: IDfNode | null = (Day as IDfNode).querySelector(".dayhead");
            if (Head !== null) { this.Note(Head as IDfNode); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.OnKey");
        }
    };

    private Note = (Head: IDfNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Day: IDfNode | null = Head.closest(".day");
            if (Day === null) { return; }
            const Box: IDfNode | null = (Day as IDfNode).querySelector(".daybox");
            if (Box === null) { return; }
            this.Pending = new AppDayPending(Box as IDfNode, Day as IDfNode, Head.getBoundingClientRect().top);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.Note");
        }
    };

    /** The day changed: remember the owner's choice and put its label back where it was. */
    public OnChange = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Box: IDfNode | null = AppDayFold.NodeOf(Ev);
            if (Box === null || nz(Box.className).indexOf("daybox") < 0) { return; }
            const Id: string = nz(Box.id);
            if (AppDayFold.IdPattern.test(Id) === true) {
                if (AppDayFold.Memory[this.Page] === undefined) { AppDayFold.Memory[this.Page] = {}; }
                AppDayFold.Memory[this.Page][Id] = Box.checked === true;
            }
            if (this.Pending === null || (this.Pending as AppDayPending).Box !== Box) { this.Pending = null; return; }
            this.Settle();
            if (this.Win !== null) { (this.Win as IDfWin).requestAnimationFrame(this.SettleLater); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.OnChange");
        }
    };

    /** One frame later: whatever the browser's own scroll anchoring did, the label ends where it was. */
    public SettleLater = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Settle();
            this.Pending = null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.SettleLater");
        }
    };

    /**
     * Scroll the conversation so the toggled day starts where its label was. ⚠ The DAY's top is where its label
     * lives (the checkbox before it is out of flow); the label itself may be pinned (`position:sticky`).
     */
    private Settle = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Pending === null || this.Doc === null) { return; }
            const Chat: IDfNode | null = (this.Doc as IDfDoc).querySelector(".chat");
            if (Chat === null) { return; }
            const Moved: number = (this.Pending as AppDayPending).Day.getBoundingClientRect().top - (this.Pending as AppDayPending).Top;
            if (Math.abs(Moved) < 0.5) { return; }
            (Chat as IDfNode).scrollTop = (Chat as IDfNode).scrollTop + Moved;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.Settle");
        }
    };

    /** The element id a `#fragment` names; a fragment that is not valid percent-encoding is taken as written. */
    public static IdOf = (Hash: string): string => {
        const Raw: string = nz(Hash).substring(1);
        try {
            return decodeURIComponent(Raw);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an address typed or pasted with a stray "%" is outside input, not a defect -
            // the fragment as written is the correct answer (and matches no bubble id, which is also correct).
            return Raw;
        }
    };

    /** The event's target as a node that can answer `closest`, or null. */
    private static NodeOf = (Ev: Event): IDfNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Target: unknown = Ev.target;
            if (Target === null || typeof Target !== "object") { return null; }
            if (typeof (Target as Record<string, unknown>).closest !== "function") { return null; }
            return Target as IDfNode;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppDayFold.NodeOf");
            return null;
        }
    };
}
